/**
 * Opt-in tenant filled-CTA overlay.
 * Does not mutate MUI palette.primary / palette.secondary.
 * Default AppButton / primaryActionSx remain platform-owned.
 *
 * Fill identity is the tenant hex. Foreground is product branding white
 * ({@link ACCESSIBLE_FOREGROUND_WHITE}) — not WCAG-gated admission.
 * Invalid / missing hex still fail-closes to null (platform CTA).
 * Hover / active are same-hue darken of the fill. White-on-fill contrast
 * is advisory, not an admission requirement.
 *
 * Surface treatment is lighting/elevation on top of the exact tenant fill:
 * a restrained same-family tonal wash plus a low-opacity white gloss.
 * The configured hex stays the dominant color — not a brightened variant.
 */

import { alpha, darken, getContrastRatio, lighten } from "@mui/material/styles";
import { ACCESSIBLE_FOREGROUND_WHITE } from "@/theme/accessible-on-fill";
import { primaryActionSx } from "@/theme/primary-action";
import { isValidHexColor } from "@/theme/tenant-branding";

/** Outlined-secondary paper AA only. Filled primary admission does not use this. */
const MIN_AA = 4.5;
const HOVER_DARKEN = 0.12;
const ACTIVE_DARKEN = 0.2;
/** Supporting tonal wash only. Fill occupies most of the button body. */
const TONAL_LIFT = 0.03;
const TONAL_SINK = 0.04;
/** Top gloss — white lighting, not a second brand color. */
const GLOSS_PEAK = 0.14;
const GLOSS_MID = 0.05;
const GLOSS_ACTIVE_PEAK = 0.07;
const EDGE_REST = 0.14;
const EDGE_HOVER = 0.16;
const EDGE_ACTIVE = 0.06;
const SHADOW_REST = 0.16;
const SHADOW_HOVER = 0.22;
const SHADOW_ACTIVE = 0.12;

export type TenantActionTokens = {
  fill: string;
  foreground: string;
  fillHover: string;
  fillActive: string;
};

function meetsAa(background: string, foreground: string): boolean {
  try {
    return getContrastRatio(background, foreground) >= MIN_AA;
  } catch {
    return false;
  }
}

/**
 * Resolved tenant filled-CTA tokens, or `null` to keep the platform CTA.
 * Valid `#RRGGBB` is admitted with branding-white foreground.
 * Invalid / missing / unreadied callers receive `null`.
 */
export function resolveTenantActionTokens(
  themeColor?: string | null,
): TenantActionTokens | null {
  if (!isValidHexColor(themeColor)) return null;

  const fill = themeColor!.trim();
  return {
    fill,
    foreground: ACCESSIBLE_FOREGROUND_WHITE,
    fillHover: darken(fill, HOVER_DARKEN),
    fillActive: darken(fill, ACTIVE_DARKEN),
  };
}

/**
 * Outlined tenant CTA tokens. The brand color is ink on a light surface,
 * so the fill must keep 4.5:1 against canonical white (paper).
 * Light golds that work as a filled CTA with branding-white text still fail here.
 */
export function resolveTenantOutlinedActionTokens(
  themeColor?: string | null,
): TenantActionTokens | null {
  const tokens = resolveTenantActionTokens(themeColor);
  if (!tokens) return null;
  if (
    !meetsAa(tokens.fill, ACCESSIBLE_FOREGROUND_WHITE) ||
    !meetsAa(tokens.fillHover, ACCESSIBLE_FOREGROUND_WHITE) ||
    !meetsAa(tokens.fillActive, ACCESSIBLE_FOREGROUND_WHITE)
  ) {
    return null;
  }
  return tokens;
}

function glossLayer(peak: number): string {
  return `linear-gradient(180deg, rgba(255, 255, 255, ${peak}) 0%, rgba(255, 255, 255, ${GLOSS_MID}) 12%, rgba(255, 255, 255, 0) 36%)`;
}

function tonalGradient(base: string): string {
  const start = lighten(base, TONAL_LIFT);
  const end = darken(base, TONAL_SINK);
  return `linear-gradient(180deg, ${start} 0%, ${base} 20%, ${base} 70%, ${end} 100%)`;
}

function surfaceLayers(base: string, glossPeak: number): string {
  return `${glossLayer(glossPeak)}, ${tonalGradient(base)}`;
}

function brandShadow(fill: string, opacity: number, yPx: number, blurPx: number): string {
  return `0 ${yPx}px ${blurPx}px ${alpha(fill, opacity)}`;
}

function edgeHighlight(opacity: number): string {
  return `inset 0 1px 0 rgba(255, 255, 255, ${opacity})`;
}

function elevationShadow(
  fill: string,
  yPx: number,
  blurPx: number,
  fillOpacity: number,
  insetOpacity: number,
): string {
  return `${edgeHighlight(insetOpacity)}, ${brandShadow(fill, fillOpacity, yPx, blurPx)}`;
}

/**
 * Overlay sx for an already-resolved tenant filled CTA.
 * Disabled and focus-visible match {@link primaryActionSx} so opt-in
 * actions keep the same unavailable / keyboard treatment as platform CTAs.
 *
 * Token identity is owned by {@link resolveTenantBranding}; this helper
 * only paints already-resolved tokens. Gloss and brand shadow are lighting
 * on those tokens — not from Theme Color or a second CTA system.
 */
export function tenantActionCtaSx(tokens: TenantActionTokens) {
  return {
    bgcolor: tokens.fill,
    backgroundImage: surfaceLayers(tokens.fill, GLOSS_PEAK),
    color: tokens.foreground,
    border: "none" as const,
    boxShadow: elevationShadow(tokens.fill, 2, 8, SHADOW_REST, EDGE_REST),
    "&:hover": {
      bgcolor: tokens.fillHover,
      backgroundImage: surfaceLayers(tokens.fillHover, GLOSS_PEAK),
      boxShadow: elevationShadow(tokens.fill, 4, 12, SHADOW_HOVER, EDGE_HOVER),
    },
    "&:active": {
      bgcolor: tokens.fillActive,
      backgroundImage: surfaceLayers(tokens.fillActive, GLOSS_ACTIVE_PEAK),
      boxShadow: elevationShadow(tokens.fill, 1, 3, SHADOW_ACTIVE, EDGE_ACTIVE),
    },
    "&.Mui-disabled": {
      ...primaryActionSx["&.Mui-disabled"],
      backgroundImage: "none",
      boxShadow: "none",
    },
    "&:focus-visible": primaryActionSx["&:focus-visible"],
  };
}

/** Preferred name for the filled tenant overlay. Same contract as {@link tenantActionCtaSx}. */
export const tenantPrimaryCtaSx = tenantActionCtaSx;
